import React, { useState, useEffect, useRef } from 'react';
import { Capacitor } from '@capacitor/core';
import { BrowserRouter as Router, Routes, Route, useLocation, useNavigate, Link, Navigate } from 'react-router-dom';
import { LanguageProvider } from './contexts/LanguageContext';
import CapacitorStorage from './services/CapacitorStorage';
import { initializeModalViewportFix } from './utils/modalViewportFix';
import { SessionStorage } from './utils/unifiedStorage';
import AndroidErrorHandler from './utils/androidErrorHandler';
import {
  parseSessionExpiry,
  isSessionExpiryValid,
  writeSessionExpiry,
  clearSessionStorageKeys,
  SESSION_USER_KEY,
} from './utils/sessionExpiry';
import { MPIN_ACCESS_GLOBALLY_DISABLED } from './utils/mpinAccessUtils';

// 🎯 CORE BUSINESS COMPONENTS (Required)
import Dashboard from './components/Dashboard';

// 🔧 DEVELOPER DEMO COMPONENTS (Development Only)
import BackgroundRemoverTest from './BackgroundRemoverTest';
import Items from './components/Items';
import Parties from './components/Parties';
import Sales from './components/Sales';
import SalesDev from './components/SalesDev';
import ServiceOperations from './components/ServiceOperations';
import ServiceTags from './components/ServiceTags';
import Purchases from './components/Purchases';
import PurchasesDev from './components/PurchasesDev';
import SubscriptionTestPanel from './components/SubscriptionTestPanel';
import Payments from './components/Payments';
import Expenses from './components/Expenses';
import FixedAssets from './components/FixedAssets';
import Investments from './components/Investments';
import Loans from './components/Loans';
import Manufacturing from './components/ManufacturingNew';
import WorkOrders from './components/WorkOrders/WorkOrders';
import { ENABLE_WORK_ORDERS } from './config/featureFlags';
import Reports from './components/Reports';
import BusinessAnalytics from './components/BusinessAnalytics';
import Taxes from './components/Taxes';
import BillTemplates from './components/BillTemplates';
import LayoutDocumentBuilder from './components/LayoutDocumentBuilder';
import Settings from './components/Settings';
import UserProfile from './components/UserProfile';
import LoginSettings from './components/LoginSettings';
import MobileSettings from './components/MobileSettings';
import Login from './components/Login';
import NewLogin from './components/NewLogin';
import MpinRelogin from './components/MpinRelogin';
import ErrorBoundary from './components/ErrorBoundary';
import AuthErrorBoundary from './components/AuthErrorBoundary';
// 🧪 TESTING: Error Handler Test Panel (REMOVE BEFORE PRODUCTION)
import ErrorHandlerTestPanel from './components/ErrorHandlerTestPanel';

// 🏢 COMPANY & SYSTEM COMPONENTS
import CompanyDetails from './components/CompanyDetails';
import CompanySwitcher from './components/CompanySwitcher';
import ImageManager from './components/ImageManager';

// 🔐 MPIN SECURITY COMPONENTS
import MPINSetup from './components/MPINSetup';
import MPINVerification from './components/MPINVerification';
import { withMPINProtection } from './components/MPINProtectedRoute';
import MPINAccessControl from './components/MPINAccessControl';
import VerificationRequired from './components/VerificationRequired';
import ProtectedRoute from './components/ProtectedRoute';

// 👥 ENHANCED EMPLOYEE MANAGEMENT
import EmployeeManagement from './components/EmployeeManagement';

// 🔧 DEVICE MANAGEMENT
import DeviceManagement from './components/DeviceManagement';

// 🏢 ENHANCED COMPANY CONTEXT
import { EnhancedCompanyProvider, useEnhancedCompany } from './contexts/EnhancedCompanyContext';
import { RefreshProvider } from './contexts/RefreshContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { LoadingProvider } from './contexts/LoadingContext';
import LoadingScreen from './components/LoadingScreen';
import AcctooSplashIntro from './components/AcctooSplashIntro';
import Upgrades from './components/Upgrades';
import SubscriptionStatus from './components/SubscriptionStatus';
import SubscriptionStatusModern from './components/Subscription/SubscriptionStatusModern';
import TopupPurchase from './components/TopupPurchase';
import PlanUpgradeUser from './components/PlanUpgradeUser';

// 📱 MOBILE & NAVIGATION COMPONENTS (Important Features)
import MobileBottomNav from './components/MobileBottomNav';
import CustomPullToRefresh from './components/CustomPullToRefresh';
import RefreshButton from './components/RefreshButton';
import MobilePermissions from './components/MobilePermissions';
import PostLoginPermissions from './components/PostLoginPermissions';

// 🔄 CONVERTER COMPONENTS

// 📴 OFFLINE MODE COMPONENTS
import OfflineIndicator from './components/OfflineIndicator';
// OwnerReports and OfflineModeHelp excluded from production deployment
import FloatingConverter from './components/FloatingConverter';
import PermissionsGate from './components/PermissionsGate';

// Electron-specific components
import ElectronNavigationToolbar from './components/ElectronNavigationToolbar';
import { TabProvider } from './components/ElectronTabManager';

// 🎣 HOOKS
import { useUnreadReports } from './hooks/useUnreadReports';
import { useSafeAreaInsets } from './hooks/useSafeAreaInsets';

// 📝 REPORT TO OWNER COMPONENTS
import ReportToOwner from './components/ReportToOwner';
import ReportedIssues from './components/ReportedIssues';
import ResolvedIssues from './components/ResolvedIssues';

// 🆘 HELP & SUPPORT COMPONENTS (Required)


// 📊 DATA EXPORT COMPONENTS (Company-wise Export)
import DataExport from './components/DataExport';
import BackupRestore from './components/BackupRestore';
import SimpleImport from './components/SimpleImport';

// 📥 IMPORT COMPONENTS (Page-specific Import)
import ImportSelection from './components/Import/ImportSelection';
import PartiesImport from './components/Import/PartiesImport';
import SalesImport from './components/Import/SalesImport';
import PurchasesImport from './components/Import/PurchasesImport';
import ExpensesImport from './components/Import/ExpensesImport';

// 🚦 SUBSCRIPTION LIMITS & ALERTS
import { UsageAlertsContainer } from './components/UsageAlerts';
import UsagePage from './pages/UsagePage';
import CompanyUsageStatisticsPage from './pages/CompanyUsageStatisticsPage';

import { 
  signOut, 
  onAuthStateChanged,
  getAuth,
  applyActionCode,
  reload
} from 'firebase/auth';
import { 
  doc, 
  getDoc,
  setDoc,
  serverTimestamp
} from 'firebase/firestore';
import { setStoredAppId } from './utils/appPath';
import { needsMpinRelogin, setSessionExpiry, clearSession } from './utils/mpinManager';
// import { auth, db, googleProvider } from './firebase.config';
import { db as firebaseDb, auth as firebaseAuth } from './firebase.config';
import { signInFromPublicSSO } from './utils/ssoClient';
import { heartbeat } from './services/DeviceService';
import { resetPermissionPreferences } from './utils/permissionUtils';
// Optional SSO (disabled by default)
const ENABLE_PUBLIC_SSO = process.env.REACT_APP_ENABLE_PUBLIC_SSO === 'true';
// import { getCompanyInfo } from './utils/companyUtils';
// import { getSettingsDoc } from './utils/appArtifacts';
// import './utils/runMigration';

const mockDb = null;

  // Wrapper component to handle location-based navigation
function AppContent({ user, isAuthReady, ...props }) {
  const location = useLocation();
  const navigate = useNavigate();
  // 🧪 TESTING: Include test-errors as public page (REMOVE BEFORE PRODUCTION)
  const isOnPublicPage =
    location.pathname === '/' ||
    location.pathname === '/test-errors' ||
    location.pathname === '/login' ||
    location.pathname === '/old-login' ||
    location.pathname === '/verification-required';
  const { setShowWizardForFirstTime, openWizardManually, isCurrentUserOwner } = useEnhancedCompany();
  const { companyInfo, hasFeature } = useEnhancedCompany();
  
  // Get safe area insets for Android status bar and navigation bar
  const safeAreaInsets = useSafeAreaInsets();

  // Handle email verification link (global handler for any route)
  // SECURITY: Prevents duplicate processing and validates user ownership
  useEffect(() => {
    // Flag to prevent duplicate processing if multiple handlers are active
    let isProcessing = false;
    
    const handleEmailVerification = async () => {
      // Prevent duplicate processing
      if (isProcessing) {
        console.log('⚠️ Email verification already in progress, skipping...');
        return;
      }
      
      try {
        // Check for email verification query parameters in URL (both query string and hash)
        const urlParams = new URLSearchParams(window.location.search);
        const hash = window.location.hash;
        const hashParams = new URLSearchParams(hash.split('?')[1] || '');
        
        const mode = urlParams.get('mode') || hashParams.get('mode');
        const oobCode = urlParams.get('oobCode') || hashParams.get('oobCode');
        
        // Validate parameters
        if (!mode || !oobCode) return;
        if (mode !== 'verifyEmail') return;
        if (oobCode.length < 20 || oobCode.length > 500) {
          console.error('❌ Invalid oobCode length');
          return;
        }
        
        if (mode === 'verifyEmail' && oobCode) {
          isProcessing = true;
          // Set flag to prevent Login.js from also processing this
          sessionStorage.setItem('email_verification_processed', 'true');
          console.log('📧 Email verification link detected in App.js, processing...');
          
          // SECURITY: Store current user email before verification (if logged in)
          const currentUserBefore = firebaseAuth.currentUser;
          const currentUserEmailBefore = currentUserBefore?.email;
          
          try {
            // Apply the action code to verify the email
            // Firebase validates the code server-side
            await applyActionCode(firebaseAuth, oobCode);
            console.log('✅ Email verified successfully!');
            
            // SECURITY: Validate email ownership if user is logged in
            // Reload the current user to get updated emailVerified status
            if (firebaseAuth.currentUser) {
              await firebaseAuth.currentUser.reload();
              const verifiedEmail = firebaseAuth.currentUser.email;
              
              // If user was logged in before verification, verify the email matches
              if (currentUserEmailBefore && verifiedEmail !== currentUserEmailBefore) {
                console.error('🚨 SECURITY ALERT: Email verification mismatch!', {
                  loggedInAs: currentUserEmailBefore,
                  verifiedEmail: verifiedEmail
                });
                // This is a security issue - the verification link was for a different user
                // Log out the current user for security
                await signOut(firebaseAuth);
                navigate('/login?error=verification_mismatch', { replace: true });
                return;
              }
              
              console.log('✅ User reloaded, emailVerified:', firebaseAuth.currentUser.emailVerified);
              
              // Update user document in Firestore with retry logic
              // CRITICAL: Ensure users document exists with emailVerified for Firestore rules fallback
              let retries = 3;
              let userDocUpdated = false;
              while (retries > 0) {
                try {
                  const userRef = doc(firebaseDb, 'users', firebaseAuth.currentUser.uid);
                  const userSnap = await getDoc(userRef);
                  const existingData = userSnap.exists() ? userSnap.data() : {};
                  
                  // Create or update user document with all required fields
                  const userData = {
                    uid: firebaseAuth.currentUser.uid,
                    email: firebaseAuth.currentUser.email || existingData.email || '',
                    displayName: firebaseAuth.currentUser.displayName || existingData.displayName || '',
                    phoneNumber: firebaseAuth.currentUser.phoneNumber || existingData.phoneNumber || '',
                    emailVerified: true, // CRITICAL: Set to true for Firestore rules
                    updatedAt: serverTimestamp(),
                    createdAt: existingData.createdAt || serverTimestamp(),
                    ...existingData // Preserve any existing fields
                  };
                  
                  await setDoc(userRef, userData, { merge: true });
                  
                  // Verify the document was created/updated correctly
                  const verifySnap = await getDoc(userRef);
                  if (verifySnap.exists() && verifySnap.data().emailVerified === true) {
                    console.log('✅ User document created/updated with emailVerified status:', {
                      uid: firebaseAuth.currentUser.uid,
                      emailVerified: verifySnap.data().emailVerified,
                      email: verifySnap.data().email
                    });
                    userDocUpdated = true;
                    break; // Success, exit retry loop
                  } else {
                    throw new Error('User document verification failed - emailVerified not set correctly');
                  }
                } catch (firestoreError) {
                  retries--;
                  if (retries === 0) {
                    console.error('⚠️ Could not update Firestore user document after retries:', firestoreError);
                    // Don't fail verification if Firestore update fails - Firebase Auth is the source of truth
                  } else {
                    console.warn(`⚠️ Firestore update failed, retrying... (${retries} attempts left)`, firestoreError);
                    await new Promise(resolve => setTimeout(resolve, 1000)); // Wait 1s before retry
                  }
                }
              }
              
              if (!userDocUpdated) {
                console.warn('⚠️ WARNING: User document was not updated successfully. Firestore rules may deny access.');
              }
            }
            
            // Clear URL parameters
            const cleanUrl = window.location.pathname + (window.location.hash.split('?')[0] || '');
            window.history.replaceState({}, document.title, cleanUrl);
            
            // Clear the processed flag after a delay
            setTimeout(() => {
              sessionStorage.removeItem('email_verification_processed');
            }, 5000);
            
            // If user is logged in, redirect to dashboard
            if (firebaseAuth.currentUser) {
              navigate('/dashboard', { replace: true });
            } else {
              // If not logged in, redirect to login page
              navigate('/login?error=verification_success', { replace: true });
            }
          } catch (error) {
            console.error('❌ Error verifying email:', error);
            let errorParam = 'verification_failed';
            
            if (error.code === 'auth/expired-action-code') {
              errorParam = 'verification_expired';
            } else if (error.code === 'auth/invalid-action-code') {
              errorParam = 'verification_invalid';
            } else if (error.code === 'auth/user-disabled') {
              errorParam = 'account_disabled';
            }
            
            // Redirect to login page with error message
            navigate(`/login?error=${errorParam}`, { replace: true });
          } finally {
            isProcessing = false;
          }
        }
      } catch (error) {
        console.error('Error in email verification handler:', error);
        isProcessing = false;
      }
    };
    
    handleEmailVerification();
  }, [navigate]);

  // Session restoration on app start
  useEffect(() => {
    const restoreSession = async () => {
      try {
        // Initialize Android error handler
        AndroidErrorHandler.init();
        
        if (!user && isAuthReady) {
          // Try to restore session from unified storage
          const userData = await SessionStorage.getUserData();
          const authToken = await SessionStorage.getAuthToken();
          
          if (userData && authToken) {
            console.log('🔄 Session restored from storage:', userData);
            // Session will be automatically restored by Firebase Auth
          }
        }
      } catch (error) {
        console.error('Session restoration error:', error);
        AndroidErrorHandler.handleAndroidError(error, 'Session Restoration');
      }
    };

    restoreSession();
  }, [user, isAuthReady]);

  // Redirect authenticated users from root path to dashboard
  // 🧪 TESTING: Exclude /test-errors from redirect (REMOVE BEFORE PRODUCTION)
  useEffect(() => {
    if (user && isAuthReady && (location.pathname === '/' || location.pathname === '/login') && location.pathname !== '/test-errors') {
      console.log('🔄 Redirecting authenticated user from', location.pathname, 'to dashboard');
      console.log('🔄 User verified status:', user.isVerified);
      // Only redirect if user is verified
      if (user.isVerified !== false) {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, isAuthReady, location.pathname, navigate]);
  const [companyWizardOpen, setCompanyWizardOpen] = useState(false);
  const [companyWizardData, setCompanyWizardData] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showDeveloperMenu, setShowDeveloperMenu] = useState(false);
  const userMenuRef = useRef(null);
  const [showMpinRelogin, setShowMpinRelogin] = useState(false);
  
  // Converter states
  const [showConverter, setShowConverter] = useState(false);
  const [converterType, setConverterType] = useState('currency');

  // Listen for mobile converter events
  useEffect(() => {
    const handleOpenConverter = (event) => {
      setConverterType(event.detail.type);
      setShowConverter(true);
    };

    window.addEventListener('openConverter', handleOpenConverter);
    return () => window.removeEventListener('openConverter', handleOpenConverter);
  }, []);

  // Unread reports hook
  const { unreadEmployeeReports, unreadOwnerReports, isOwner, markOwnerReportsAsRead } = useUnreadReports();
  
  
  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  // Session Management & Auto-login (extend only when already valid)
  useEffect(() => {
    const checkSession = () => {
      try {
        const cachedUser = localStorage.getItem(SESSION_USER_KEY);
        if (cachedUser && isSessionExpiryValid()) {
          console.log('✅ Valid session found, extending...');
          writeSessionExpiry();
          setSessionExpiry(720); // MPIN helper keys (30 days)
        } else if (cachedUser && !isSessionExpiryValid()) {
          console.log('⚠️ Session expired, clearing...');
          clearSessionStorageKeys();
        }
      } catch (error) {
        console.error('Session check error:', error);
      }
    };
    
    checkSession();
  }, []);

  // Check for MPIN relogin requirement (check BEFORE refreshing expiry)
  useEffect(() => {
    if (user && user.uid) {
      const needsRelogin = needsMpinRelogin(user.uid);
      if (needsRelogin && !MPIN_ACCESS_GLOBALLY_DISABLED) {
        setShowMpinRelogin(true);
      } else {
        setSessionExpiry(720); // refresh only when not prompting for MPIN
      }
    }
  }, [user]);


  // Heartbeat: update device lastSeen every 60s when user is logged in and company context available
  useEffect(() => {
    let intervalId;
    if (user && user.uid) {
      intervalId = setInterval(async () => {
        try {
          const activeCompanyId = localStorage.getItem('acctoo_last_company');
          if (activeCompanyId) {
            await heartbeat({ userId: user.uid, companyId: activeCompanyId });
          }
        } catch (_) {}
      }, 60000);
    }
    return () => { if (intervalId) clearInterval(intervalId); };
  }, [user?.uid]);


  // Handle MPIN relogin success
  const handleMpinReloginSuccess = () => {
    setShowMpinRelogin(false);
    // Reset session expiry
    setSessionExpiry(720); // 30 days
  };

  // Handle MPIN relogin cancel (logout)
  const handleMpinReloginCancel = () => {
    setShowMpinRelogin(false);
    clearSession();
  };
  
  // Email verified, Google, or Firebase Phone Auth only (Auth.phoneNumber / phone provider).
  // Firestore-only phone does NOT count.
  const hasAnyVerification = user && (
    user.isVerified === true ||
    user.emailVerified === true ||
    user.phoneAuthVerified === true ||
    user.providerData?.some((p) => p.providerId === 'google.com' || p.providerId === 'phone')
  );
  
  const needsVerification = user && !hasAnyVerification && user.email;
  
  // If user is logged in but not verified, show verification page
  // Block company creation until verified (prevents unverified onboarding)
  if (needsVerification && !isOnPublicPage && location.pathname !== '/verification-required') {
    console.log('📧 User needs verification, showing VerificationRequired page', {
      isVerified: user.isVerified,
      emailVerified: user.emailVerified,
      hasEmail: !!user.email,
      hasPhone: !!user.phoneNumber,
      hasGoogle: user.providerData?.some(p => p.providerId === 'google.com'),
      hasAnyVerification: hasAnyVerification
    });
    return <VerificationRequired user={user} onVerificationComplete={async () => {
      // Force refresh auth state before redirecting
      console.log('🎉 Verification completed, refreshing auth state...');
      
      // Reload user to get latest verification status
      if (firebaseAuth.currentUser) {
        try {
          await reload(firebaseAuth.currentUser);
          console.log('✅ User reloaded, emailVerified:', firebaseAuth.currentUser.emailVerified);
          
          // CRITICAL: Update persistent session with new emailVerified status
          // This prevents stale data from being restored on next page load
          const persistentUser = localStorage.getItem('acctoo_user');
          if (persistentUser) {
            try {
              const persistentUserData = JSON.parse(persistentUser);
              const storedUid = persistentUserData.uid || persistentUserData.userId || persistentUserData.firebaseUID;
              if (storedUid === firebaseAuth.currentUser.uid) {
                persistentUserData.emailVerified = firebaseAuth.currentUser.emailVerified;
                persistentUserData.phoneNumber = firebaseAuth.currentUser.phoneNumber || persistentUserData.phoneNumber || '';
                persistentUserData.isVerified = !!(firebaseAuth.currentUser.emailVerified ||
                  firebaseAuth.currentUser.phoneNumber ||
                  firebaseAuth.currentUser.providerData?.some(p => p.providerId === 'google.com' || p.providerId === 'phone'));
                localStorage.setItem('acctoo_user', JSON.stringify(persistentUserData));
                console.log('✅ Persistent session updated with new verification status');
              }
            } catch (updateError) {
              console.error('Error updating persistent session:', updateError);
            }
          }
        } catch (error) {
          console.error('Error reloading user:', error);
        }
      }
      
      // Force trigger auth state change to update user state in App.js
      // This ensures the user state is updated before redirect
      if (firebaseAuth.currentUser) {
        // Trigger a manual auth state update by calling the auth state change handler
        // We'll use a small delay to ensure the state propagates
        setTimeout(() => {
          console.log('🔄 Redirecting to dashboard...');
          // Use navigate instead of window.location to avoid full page reload
          // This allows React state to update properly
          window.location.href = '/dashboard';
        }, 1000);
      } else {
        window.location.href = '/dashboard';
      }
    }} />;
  }
  
  return (
    <div className="min-h-screen bg-gray-100">
        {/* 🚦 Subscription Limits Alert System */}
        {user && !isOnPublicPage && companyInfo?.companyId && (
          <UsageAlertsContainer 
            companyId={companyInfo.companyId}
            onUpgrade={() => navigate('/pricing')}
          />
        )}

        {/* Electron Navigation Toolbar - Show only when logged in */}
        {window.electronAPI && user && !isOnPublicPage && (
          <div 
            className="fixed left-0 right-0 z-50"
            style={{ top: `${safeAreaInsets.top}px` }}
          >
            <ElectronNavigationToolbar user={user} isOnPublicPage={isOnPublicPage} />
          </div>
        )}

        {/* Show authenticated navigation only when user is logged in AND not on public page */}
        {user && !isOnPublicPage && (
        <nav 
          className={`fixed left-0 right-0 z-40 bg-white shadow-lg`}
          style={{ 
            top: window.electronAPI 
              ? `${safeAreaInsets.top + 48}px` // 48px = Electron toolbar height (top-12 = 3rem)
              : `${safeAreaInsets.top}px` 
          }}
        >
          <div className="max-w-7xl mx-auto px-2 sm:px-4">
            <div className="flex justify-between items-center h-16 min-w-0">
              {/* Logo and Brand */}
              <Link to="/dashboard" className="flex items-center hover:opacity-80 transition-opacity duration-200 min-w-0 flex-shrink">
                <img src="/acctoo-logo.png" alt="ACCTOO Logo" className="h-7 sm:h-8 w-auto mr-2 sm:mr-3 flex-shrink-0" />
                <span className="hidden sm:inline text-lg sm:text-xl font-bold text-blue-600 truncate">ACCTOO</span>
              </Link>
              
              {/* 🎯 DESKTOP NAVIGATION WITH DROPDOWNS (Keep This Feature) */}
              <div className="hidden lg:flex items-center space-x-4">
                {/* Core Business */}
                <div className="relative group">
                  <button className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium flex items-center">
                    Business
                    <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="py-1">
                      <Link to="/items" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Items</Link>
                      <Link to="/parties" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Parties</Link>
                      <Link to="/sales" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Sales</Link>
                      <Link to="/purchases" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Purchases</Link>
                      <Link to="/payments" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Payments</Link>
                      <Link to="/expenses" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Expenses</Link>
                      {ENABLE_WORK_ORDERS && (
                        <Link to="/work-orders" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Work Orders</Link>
                      )}
                      <Link to="/fixed-assets" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Fixed Assets</Link>
                      <Link to="/investments" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Investments</Link>
                      <Link to="/loans" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Loans</Link>
                      <Link to="/manufacturing" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Manufacturing</Link>
                      <Link to="/service-operations" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Service Operations</Link>
                      <Link to="/service-tags" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Service Tags</Link>
                      <Link to="/bill-templates" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Bill Templates</Link>
                    </div>
                  </div>
                </div>

                {/* Reports */}
                <div className="relative group">
                  <button className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium flex items-center">
                    Reports
                    <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="py-1">
                      <Link to="/reports" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">All Reports</Link>
                      <Link to="/business-analytics" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Business Analytics</Link>
                      <Link to="/taxes" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Taxes</Link>
                    </div>
                  </div>
                </div>

                {/* Company Management */}
                <div className="relative group">
                  <button className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium flex items-center">
                    Company
                    <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className="absolute left-0 mt-2 w-56 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="py-1">
                      <Link to="/company-details" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Company Details</Link>
                      <Link to="/subscription" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Subscription & Usage</Link>
                      <Link to="/usage-statistics" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Usage statistics</Link>
                      <Link to="/employee-management" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Employees</Link>
                      <Link to="/import" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Bulk Import Data</Link>
                      <Link to="/device-management" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Device Management</Link>
                      {isCurrentUserOwner() && (
                        <Link to="/owner-reports" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">Owner Reports</Link>
                      )}
                      <button
                        type="button"
                        onClick={openWizardManually}
                        className="w-full text-left block px-4 py-2 text-sm text-blue-700 hover:bg-blue-50"
                      >
                        + Create New Company
                      </button>
                    </div>
                  </div>
                </div>

                {/* Report to Owner Button */}
                {!isOwner && (
                  <Link
                    to="/report-to-owner"
                    className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium flex items-center relative"
                  >
                    Report to Owner
                    {unreadEmployeeReports > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {unreadEmployeeReports > 99 ? '99+' : unreadEmployeeReports}
                      </span>
                    )}
                  </Link>
                )}

                {/* Reported Issues Button */}
                {isOwner && (
                  <Link
                    to="/reported-issues"
                    onClick={markOwnerReportsAsRead}
                    className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium flex items-center relative"
                  >
                    Reported Issues
                    {unreadOwnerReports > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                        {unreadOwnerReports > 99 ? '99+' : unreadOwnerReports}
                      </span>
                    )}
                  </Link>
                )}


                {/* Refresh Button */}
                <RefreshButton size="sm" className="mr-2" />

                {/* Converter Dropdown */}
                <div className="relative group">
                  <button className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium flex items-center">
                    Converter
                    <svg className="ml-1 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  <div className="absolute left-0 mt-2 w-40 bg-white rounded-md shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="py-1">
                      <button
                        onClick={() => {
                          setConverterType('currency');
                          setShowConverter(true);
                        }}
                        className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        💱 Currency
                      </button>
                      <button
                        onClick={() => {
                          setConverterType('unit');
                          setShowConverter(true);
                        }}
                        className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        📏 Unit
                      </button>
                      <button
                        onClick={() => {
                          setConverterType('gst');
                          setShowConverter(true);
                        }}
                        className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                      >
                        💰 GST Calculator
                      </button>
                    </div>
                  </div>
                </div>

                {/* Company Switcher moved to user profile menu */}
              </div>

              {/* Right side controls: hamburger + user profile */}
              <div className="flex items-center">
                {/* Mobile Hamburger (navbar) */}
                <button
                  className="lg:hidden mr-2 inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-blue-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                  onClick={() => window.dispatchEvent(new Event('toggleMobileNav'))}
                  aria-label="Open navigation menu"
                >
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>

                {/* 🔧 DEVELOPER MENU - Only visible in development */}
                {process.env.NODE_ENV === 'development' && (
                  <div className="relative">
                    <button
                      onClick={() => setShowDeveloperMenu(v => !v)}
                      className="flex items-center gap-2 text-orange-600 hover:text-orange-700 px-3 py-2 rounded-md text-sm font-medium bg-orange-50 border border-orange-200 hover:bg-orange-100 focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors"
                      title="Developer Options - Development Only"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      </svg>
                      <span className="hidden sm:block">DEV</span>
                    </button>

                    {showDeveloperMenu && (
                      <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg border border-orange-200 z-50">
                        <div className="py-2">
                          <div className="px-4 py-2 border-b border-orange-200 bg-orange-50">
                            <div className="flex items-center space-x-2">
                              <svg className="h-5 w-5 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                              </svg>
                              <div>
                                <h3 className="text-sm font-semibold text-orange-900">
                                  🚧 Developer Options
                                </h3>
                                <p className="text-xs text-orange-700">
                                  New Features Testing Area
                                </p>
                              </div>
                            </div>
                          </div>

                          <div className="max-h-96 overflow-y-auto">
                            <Link
                              to="/demo"
                              onClick={() => setShowDeveloperMenu(false)}
                              className="flex items-center space-x-3 px-4 py-3 hover:bg-orange-50 transition-colors group"
                            >
                              <span className="text-lg">🎪</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 group-hover:text-orange-900">
                                  Complete Demo
                                </p>
                                <p className="text-xs text-gray-600 group-hover:text-orange-700">
                                  Test all new features together
                                </p>
                              </div>
                              <svg className="h-4 w-4 text-gray-400 rotate-[-90deg] group-hover:text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                              </svg>
                            </Link>

                            <Link
                              to="/demo?tab=export"
                              onClick={() => setShowDeveloperMenu(false)}
                              className="flex items-center space-x-3 px-4 py-3 hover:bg-orange-50 transition-colors group"
                            >
                              <span className="text-lg">📊</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 group-hover:text-orange-900">
                                  Export System
                                </p>
                                <p className="text-xs text-gray-600 group-hover:text-orange-700">
                                  Test PDF, Excel, CSV exports
                                </p>
                              </div>
                              <svg className="h-4 w-4 text-gray-400 rotate-[-90deg] group-hover:text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                              </svg>
                            </Link>

                            <Link
                              to="/demo?tab=templates"
                              onClick={() => setShowDeveloperMenu(false)}
                              className="flex items-center space-x-3 px-4 py-3 hover:bg-orange-50 transition-colors group"
                            >
                              <span className="text-lg">🎨</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 group-hover:text-orange-900">
                                  Template System
                                </p>
                                <p className="text-xs text-gray-600 group-hover:text-orange-700">
                                  JSON-based invoice templates
                                </p>
                              </div>
                              <svg className="h-4 w-4 text-gray-400 rotate-[-90deg] group-hover:text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                              </svg>
                            </Link>

                            <Link
                              to="/demo?tab=import"
                              onClick={() => setShowDeveloperMenu(false)}
                              className="flex items-center space-x-3 px-4 py-3 hover:bg-orange-50 transition-colors group"
                            >
                              <span className="text-lg">📥</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 group-hover:text-orange-900">
                                  Import Wizard
                                </p>
                                <p className="text-xs text-gray-600 group-hover:text-orange-700">
                                  Tally, Marg, Vyapar support
                                </p>
                              </div>
                              <svg className="h-4 w-4 text-gray-400 rotate-[-90deg] group-hover:text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                              </svg>
                            </Link>

                            <Link
                              to="/demo?tab=forms"
                              onClick={() => setShowDeveloperMenu(false)}
                              className="flex items-center space-x-3 px-4 py-3 hover:bg-orange-50 transition-colors group"
                            >
                              <span className="text-lg">📝</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 group-hover:text-orange-900">
                                  Step Forms
                                </p>
                                <p className="text-xs text-gray-600 group-hover:text-orange-700">
                                  Wizard-based party creation
                                </p>
                              </div>
                              <svg className="h-4 w-4 text-gray-400 rotate-[-90deg] group-hover:text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                              </svg>
                            </Link>

                            <Link
                              to="/demo?tab=barcode"
                              onClick={() => setShowDeveloperMenu(false)}
                              className="flex items-center space-x-3 px-4 py-3 hover:bg-orange-50 transition-colors group"
                            >
                              <span className="text-lg">📦</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 group-hover:text-orange-900">
                                  Barcode System
                                </p>
                                <p className="text-xs text-gray-600 group-hover:text-orange-700">
                                  Item tracking & bulk generation
                                </p>
                              </div>
                              <svg className="h-4 w-4 text-gray-400 rotate-[-90deg] group-hover:text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                              </svg>
                            </Link>

                            <Link
                              to="/templates"
                              onClick={() => setShowDeveloperMenu(false)}
                              className="flex items-center space-x-3 px-4 py-3 hover:bg-orange-50 transition-colors group"
                            >
                              <span className="text-lg">🔧</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 group-hover:text-orange-900">
                                  Templates Only
                                </p>
                                <p className="text-xs text-gray-600 group-hover:text-orange-700">
                                  Test templates independently
                                </p>
                              </div>
                              <svg className="h-4 w-4 text-gray-400 rotate-[-90deg] group-hover:text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                              </svg>
                            </Link>

                            <Link
                              to="/sales-dev"
                              onClick={() => setShowDeveloperMenu(false)}
                              className="flex items-center space-x-3 px-4 py-3 hover:bg-orange-50 transition-colors group"
                            >
                              <span className="text-lg">🛒</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 group-hover:text-orange-900">
                                  Sales DEV
                                </p>
                                <p className="text-xs text-gray-600 group-hover:text-orange-700">
                                  Development copy of sales page
                                </p>
                              </div>
                              <svg className="h-4 w-4 text-gray-400 rotate-[-90deg] group-hover:text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                              </svg>
                            </Link>
                            
                            <Link
                              to="/purchases-dev"
                              onClick={() => setShowDeveloperMenu(false)}
                              className="flex items-center space-x-3 px-4 py-3 hover:bg-orange-50 transition-colors group"
                            >
                              <span className="text-lg">📦</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 group-hover:text-orange-900">
                                  Purchases DEV
                                </p>
                                <p className="text-xs text-gray-600 group-hover:text-orange-700">
                                  Development copy of purchases page
                                </p>
                              </div>
                              <svg className="h-4 w-4 text-gray-400 rotate-[-90deg] group-hover:text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                              </svg>
                            </Link>

                            <Link
                              to="/dev-status"
                              onClick={() => setShowDeveloperMenu(false)}
                              className="flex items-center space-x-3 px-4 py-3 hover:bg-orange-50 transition-colors group"
                            >
                              <span className="text-lg">📈</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 group-hover:text-orange-900">
                                  Development Status
                                </p>
                                <p className="text-xs text-gray-600 group-hover:text-orange-700">
                                  Feature development progress
                                </p>
                              </div>
                              <svg className="h-4 w-4 text-gray-400 rotate-[-90deg] group-hover:text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                              </svg>
                            </Link>

                            <Link
                              to="/bg-remover"
                              onClick={() => setShowDeveloperMenu(false)}
                              className="flex items-center space-x-3 px-4 py-3 hover:bg-orange-50 transition-colors group"
                            >
                              <span className="text-lg">🎨</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 group-hover:text-orange-900">
                                  Background Remover
                                </p>
                                <p className="text-xs text-gray-600 group-hover:text-orange-700">
                                  HD quality background removal tool
                                </p>
                              </div>
                              <svg className="h-4 w-4 text-gray-400 rotate-[-90deg] group-hover:text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                              </svg>
                            </Link>

                            <button
                              onClick={async () => {
                                setShowDeveloperMenu(false);
                                const result = await resetPermissionPreferences();
                                if (result.success) {
                                  alert('✅ Permission preferences reset! Permission dialogs will show again.');
                                } else {
                                  alert('❌ Failed to reset permission preferences: ' + result.error);
                                }
                              }}
                              className="flex items-center space-x-3 px-4 py-3 hover:bg-orange-50 transition-colors group w-full text-left"
                            >
                              <span className="text-lg">🔓</span>
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 group-hover:text-orange-900">
                                  Reset Permission Skip
                                </p>
                                <p className="text-xs text-gray-600 group-hover:text-orange-700">
                                  Allow permission dialogs to show again
                                </p>
                              </div>
                            </button>
                          </div>

                          <div className="px-4 py-2 border-t border-orange-200 bg-orange-50">
                            <div className="flex items-center space-x-2 text-xs text-orange-700">
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                              </svg>
                              <span>⚠️ Development Mode Only</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* User Menu */}
                <div className="relative" ref={userMenuRef}>
                <button
                  onClick={() => setShowUserMenu(v => !v)}
                  className="flex items-center gap-2 text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium"
                >
                  <div className="w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center">
                    {(user?.displayName || user?.email || 'U').charAt(0).toUpperCase()}
                  </div>
                  <span className="hidden sm:block">
                    {user?.displayName || 
                     (user?.email ? user.email.split('@')[0].charAt(0).toUpperCase() + user.email.split('@')[0].slice(1) : 'User')}
                  </span>
                  <svg className={`w-4 h-4 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </button>

                {showUserMenu && (
                  <div className="fixed left-3 right-3 top-[calc(var(--safe-area-inset-top,env(safe-area-inset-top,0px))+4.25rem)] max-h-[min(75dvh,36rem)] overflow-y-auto overflow-x-visible overscroll-contain md:absolute md:left-auto md:right-0 md:top-full md:mt-2 md:w-72 md:max-h-none bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                    <div className="p-3 border-b">
                      <div className="text-sm font-semibold text-gray-800">Account</div>
                      <div className="mt-2 flex flex-col gap-1">
                        <Link to="/user-profile" onClick={() => setShowUserMenu(false)} className="text-sm text-gray-700 hover:text-blue-600">My Profile</Link>
                        <Link to="/settings" onClick={() => setShowUserMenu(false)} className="text-sm text-gray-700 hover:text-blue-600">Settings</Link>
                        <Link to="/login-settings" onClick={() => setShowUserMenu(false)} className="text-sm text-gray-700 hover:text-blue-600">Login & Security</Link>
                        {Capacitor.isNativePlatform() && (
                          <Link to="/mobile-settings" onClick={() => setShowUserMenu(false)} className="text-sm text-gray-700 hover:text-blue-600">Mobile Settings</Link>
                        )}
                      </div>
                    </div>

                    <div className="p-3 border-b">
                      <div className="text-sm font-semibold text-gray-800 mb-2">Companies</div>
                      <div className="mb-2">
                        {/* Company Switcher - Always show */}
                        <CompanySwitcher />
                      </div>
                      <button
                        onClick={() => { openWizardManually(); setShowUserMenu(false); }}
                        className="w-full text-left text-sm text-blue-700 hover:bg-blue-50 px-2 py-1 rounded"
                      >
                        + Create New Company
                      </button>
                      <Link to="/company-details" onClick={() => setShowUserMenu(false)} className="block text-sm text-gray-700 hover:text-blue-600 px-2 py-1">
                        Manage Company
                      </Link>
                      <Link to="/upgrades" onClick={() => setShowUserMenu(false)} className="block text-sm text-gray-700 hover:text-blue-600 px-2 py-1">
                        Upgrades & Features
                      </Link>
                      <Link to="/subscription" onClick={() => setShowUserMenu(false)} className="block text-sm text-gray-700 hover:text-blue-600 px-2 py-1">
                        Subscription & Usage
                      </Link>
                      <Link to="/usage" onClick={() => setShowUserMenu(false)} className="block text-sm text-gray-700 hover:text-blue-600 px-2 py-1">
                        📊 Usage & Limits
                      </Link>
                      <Link to="/usage-statistics" onClick={() => setShowUserMenu(false)} className="block text-sm text-gray-700 hover:text-blue-600 px-2 py-1">
                        📈 Usage statistics
                      </Link>
                    </div>

                    <div className="p-3">
                      <button
                        onClick={props.handleLogout}
                        className="w-full text-left text-sm text-red-600 hover:bg-red-50 px-2 py-1 rounded"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
            </div>
          </div>
        </nav>
      )}

      {/* Main Content */}
      <div
        className={`${user && !isOnPublicPage ? 'content-with-bottom-nav mobile-page-shell' : ''}`}
        style={{
          paddingTop: user && !isOnPublicPage 
            ? (window.electronAPI 
                ? `${safeAreaInsets.top + 48 + 64}px` // Status bar + Electron toolbar (48px) + Nav bar (64px)
                : `${safeAreaInsets.top + 64}px`) // Status bar + Nav bar (64px)
            : '0px'
        }}
      >
        <CustomPullToRefresh>
          <Routes>
          {/* Public Routes */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          
          {/* 🧪 TESTING: Error Handler Test Panel - REMOVE BEFORE PRODUCTION */}
          {/* Placed here (before login) so it's accessible without authentication */}
          <Route path="/test-errors" element={<ErrorHandlerTestPanel />} />
          
          <Route path="/login" element={
            <AuthErrorBoundary authState="login">
              <NewLogin />
            </AuthErrorBoundary>
          } />
          <Route path="/old-login" element={
            <AuthErrorBoundary authState="legacy-login">
              <Login />
            </AuthErrorBoundary>
          } />
          {/* Invite route removed - UID-only employee addition */}
          <Route path="/verification-required" element={
            <AuthErrorBoundary authState="verification">
              <VerificationRequired user={user} onVerificationComplete={() => {
                // Force refresh auth state and redirect to dashboard
                console.log('🎉 Verification completed, redirecting to dashboard...');
                window.location.href = '/dashboard';
              }} />
            </AuthErrorBoundary>
          } />

          {/* Protected Routes */}
          <Route path="/dashboard" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <Dashboard db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} />
            </ProtectedRoute>
          } />
          <Route path="/items" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <Items db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} />
            </ProtectedRoute>
          } />
          <Route path="/parties" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <Parties db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} />
            </ProtectedRoute>
          } />
          <Route
            path="/sales"
            element={
              <ProtectedRoute user={user} isAuthReady={isAuthReady}>
                <Sales db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/sales-dev"
            element={
              <ProtectedRoute user={user} isAuthReady={isAuthReady}>
                <SalesDev db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-operations"
            element={
              <ProtectedRoute user={user} isAuthReady={isAuthReady}>
                <ServiceOperations db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} companyId={companyInfo?.companyId} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/service-tags"
            element={
              <ProtectedRoute user={user} isAuthReady={isAuthReady}>
                <ServiceTags db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} />
              </ProtectedRoute>
            }
          />
          <Route path="/purchases" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <Purchases db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} />
            </ProtectedRoute>
          } />
          <Route
            path="/purchases-dev"
            element={
              <ProtectedRoute user={user} isAuthReady={isAuthReady}>
                <PurchasesDev db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/test-subscription"
            element={
              <ProtectedRoute user={user} isAuthReady={isAuthReady}>
                <SubscriptionTestPanel />
              </ProtectedRoute>
            }
          />
          <Route path="/payments" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <Payments db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} />
            </ProtectedRoute>
          } />
          <Route
            path="/expenses"
            element={
              <ProtectedRoute user={user} isAuthReady={isAuthReady}>
                <Expenses db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} />
              </ProtectedRoute>
            }
          />
          {ENABLE_WORK_ORDERS ? (
            <>
              <Route
                path="/work-orders"
                element={
                  <ProtectedRoute user={user} isAuthReady={isAuthReady}>
                    <WorkOrders db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/work-orders/:workOrderId"
                element={
                  <ProtectedRoute user={user} isAuthReady={isAuthReady}>
                    <WorkOrders db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} />
                  </ProtectedRoute>
                }
              />
            </>
          ) : (
            <>
              <Route path="/work-orders" element={<Navigate to="/dashboard" replace />} />
              <Route path="/work-orders/:workOrderId" element={<Navigate to="/dashboard" replace />} />
            </>
          )}
          <Route
            path="/fixed-assets"
            element={
              <ProtectedRoute user={user} isAuthReady={isAuthReady}>
                <FixedAssets db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/investments"
            element={
              <ProtectedRoute user={user} isAuthReady={isAuthReady}>
                <Investments db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/loans"
            element={
              <ProtectedRoute user={user} isAuthReady={isAuthReady}>
                <Loans db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} />
              </ProtectedRoute>
            }
          />
          <Route path="/manufacturing" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <Manufacturing />
            </ProtectedRoute>
          } />
          <Route
            path="/reports"
            element={
              <ProtectedRoute user={user} isAuthReady={isAuthReady}>
                <Reports db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/business-analytics"
            element={
              <ProtectedRoute user={user} isAuthReady={isAuthReady}>
                <BusinessAnalytics db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reports/:reportId"
            element={
              <ProtectedRoute user={user} isAuthReady={isAuthReady}>
                <Reports db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/taxes"
            element={
              <ProtectedRoute user={user} isAuthReady={isAuthReady}>
                <Taxes db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} />
              </ProtectedRoute>
            }
          />
          <Route path="/bill-templates/dummy-bill" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <LayoutDocumentBuilder db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} />
            </ProtectedRoute>
          } />
          <Route path="/bill-templates" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <BillTemplates db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} />
            </ProtectedRoute>
          } />
          <Route path="/dummy-bill" element={<Navigate to="/bill-templates/dummy-bill" replace />} />
          <Route path="/layout-document" element={<Navigate to="/bill-templates/dummy-bill" replace />} />
          <Route path="/settings" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <Settings />
            </ProtectedRoute>
          } />
          <Route path="/usage" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <UsagePage companyId={companyInfo?.companyId} />
            </ProtectedRoute>
          } />
          <Route path="/usage-statistics" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <CompanyUsageStatisticsPage />
            </ProtectedRoute>
          } />
          <Route path="/user-profile" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <UserProfile />
            </ProtectedRoute>
          } />
          <Route path="/login-settings" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <LoginSettings userId={user?.uid} appId={props.appId || null} />
            </ProtectedRoute>
          } />
          <Route path="/mobile-settings" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <MobileSettings />
            </ProtectedRoute>
          } />
          <Route
            path="/company-details"
            element={
              <ProtectedRoute user={user} isAuthReady={isAuthReady}>
                <CompanyDetails db={props.db} userId={user?.uid} isAuthReady={isAuthReady} appId={props.appId || null} onOpenWizard={(data)=>{ setCompanyWizardData(data||{}); setCompanyWizardOpen(true); }} />
              </ProtectedRoute>
            }
          />
          <Route path="/employee-management" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <EmployeeManagement />
            </ProtectedRoute>
          } />
          {/* Device Management - EXCLUDED FROM PRODUCTION DEPLOYMENT */}
          {/* <Route path="/device-management" element={<DeviceManagement />} /> */}
          <Route path="/data-export" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <DataExport />
            </ProtectedRoute>
          } />
          <Route path="/backup-restore" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <BackupRestore db={props.db} userId={user?.uid} appId={props.appId || null} />
            </ProtectedRoute>
          } />
          <Route path="/import" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <ImportSelection />
            </ProtectedRoute>
          } />
          <Route path="/import-data" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <SimpleImport />
            </ProtectedRoute>
          } />
          <Route path="/import-parties" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <PartiesImport />
            </ProtectedRoute>
          } />
          <Route path="/import-sales" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <SalesImport />
            </ProtectedRoute>
          } />
          <Route path="/import-purchases" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <PurchasesImport />
            </ProtectedRoute>
          } />
          <Route path="/import-expenses" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <ExpensesImport />
            </ProtectedRoute>
          } />
          <Route path="/upgrades" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <Upgrades />
            </ProtectedRoute>
          } />
          <Route path="/subscription" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <SubscriptionStatusModern db={firebaseDb} />
            </ProtectedRoute>
          } />
          <Route path="/buy-topup" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <TopupPurchase onClose={() => navigate('/subscription')} />
            </ProtectedRoute>
          } />
          <Route path="/upgrade-plan" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <PlanUpgradeUser db={firebaseDb} onClose={() => navigate('/subscription')} />
            </ProtectedRoute>
          } />
          {/* Owner Report Routes */}
          <Route path="/report-to-owner" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <ReportToOwner />
            </ProtectedRoute>
          } />
          <Route path="/reported-issues" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <ReportedIssues />
            </ProtectedRoute>
          } />
          <Route path="/resolved-issues" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <ResolvedIssues />
            </ProtectedRoute>
          } />

          {/* Offline Mode Routes - EXCLUDED FROM PRODUCTION DEPLOYMENT */}
          {/* <Route path="/owner-reports" element={<OwnerReports />} /> */}
          {/* <Route path="/offline-help" element={<OfflineModeHelp />} /> */}

          {/* 🔧 DEVELOPER DEMO ROUTES - EXCLUDED FROM PRODUCTION DEPLOYMENT */}
          {/* Dev routes like /sales-dev, /purchases-dev, /test-subscription are defined above with other routes */}
          
          {/* 🎨 Background Remover - Always Available for Testing */}
          <Route path="/bg-remover" element={
            <ProtectedRoute user={user} isAuthReady={isAuthReady}>
              <BackgroundRemoverTest />
            </ProtectedRoute>
          } />

          {/* Catch all route */}
          <Route path="*" element={<Navigate to={user && isAuthReady ? '/dashboard' : '/login'} replace />} />
          </Routes>
        </CustomPullToRefresh>

        {/* Post-Login Permissions - Show once after login */}
        {user && !isOnPublicPage && <PostLoginPermissions user={user} />}
        
        {/* Mobile Bottom Navigation */}
        {user && !isOnPublicPage && <MobileBottomNav />}
        {/* First-time company setup wizard (only after login, not on public page) */}
        {user && !isOnPublicPage && <FirstTimeCompanyWizard />}
        
        {/* Offline Mode Indicator */}
        {user && <OfflineIndicator />}

        {/* Wizard modal is now integrated into CompanyDetails component */}

        {/* MPIN Relogin Modal */}
        {showMpinRelogin && (
          <MpinRelogin 
            onSuccess={handleMpinReloginSuccess}
            onCancel={handleMpinReloginCancel}
          />
        )}

        {/* Permissions Gate - Disabled in favor of PostLoginPermissions */}
        {/* {user && <PermissionsGate />} */}


        {/* Floating Converter */}
        <FloatingConverter
          isOpen={showConverter}
          onClose={() => setShowConverter(false)}
          converterType={converterType}
        />
      </div>
    </div>
  );
}

// Main App Component
function App() {
  const [user, setUser] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const [splashComplete, setSplashComplete] = useState(() => {
    try {
      // Native: skip cinematic CSS intro — Capacitor splash is enough
      if (Capacitor.isNativePlatform()) return true;
      const params = new URLSearchParams(window.location.search);
      if (params.get('splash') === '1') return false;
      return sessionStorage.getItem('acctoo_splash_played_v1') === '1';
    } catch {
      return Capacitor.isNativePlatform();
    }
  });
  const [appId, setAppId] = useState(null);
  const [db, setDb] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [persistentSessionRestored, setPersistentSessionRestored] = useState(false);

  // Initialize Capacitor Storage
  useEffect(() => {
    CapacitorStorage.initialize();
  }, []);

  // Native: hide Capacitor splash as soon as React mounts (don't stack with loaders)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const { SplashScreen } = await import('@capacitor/splash-screen');
        if (!cancelled) await SplashScreen.hide({ fadeOutDuration: 200 });
      } catch {
        /* plugin optional / already hidden */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Modal viewport fix is web-only (native Capacitor skips inside the util)
  useEffect(() => {
    if (Capacitor.isNativePlatform()) return undefined;
    initializeModalViewportFix();
    return undefined;
  }, []);

  // Timeout for auth state to prevent infinite loading (reduced to 5 seconds)
  useEffect(() => {
    const authTimeout = setTimeout(() => {
      if (!isAuthReady) {
        console.warn('⚠️ Auth state timeout after 5s, forcing ready state to prevent blank page');
        setIsAuthReady(true);
        // If no user after timeout, ensure we can still show login page
        if (!user) {
          console.log('⚠️ No user after timeout, allowing login page to render');
        }
      }
    }, 5000); // 5 second timeout (reduced from 10s)

    return () => clearTimeout(authTimeout);
  }, [isAuthReady, user]);

  // Data loading trigger function
  const triggerDataLoading = (userData, appId) => {
    console.log('🚀 Triggering data loading for user:', userData.uid, 'appId:', appId);

    // Native: fire once — delayed 1s/3s waves cause duplicate company loads and lag
    const events = Capacitor.isNativePlatform()
      ? ['userRestoredFromSession', 'userAuthenticated', 'companyDataLoad']
      : [
          'userRestoredFromSession',
          'userAuthenticated',
          'appRefresh',
          'companyDataLoad',
          'forceRefresh',
        ];

    events.forEach((eventName) => {
      window.dispatchEvent(
        new CustomEvent(eventName, {
          detail: {
            user: userData,
            appId: appId,
            timestamp: Date.now(),
          },
        })
      );
    });

    if (Capacitor.isNativePlatform()) return;

    // Web only: additional delayed triggers
    setTimeout(() => {
      events.forEach((eventName) => {
        window.dispatchEvent(
          new CustomEvent(eventName, {
            detail: {
              user: userData,
              appId: appId,
              timestamp: Date.now(),
              delayed: true,
            },
          })
        );
      });
    }, 1000);

    setTimeout(() => {
      events.forEach((eventName) => {
        window.dispatchEvent(
          new CustomEvent(eventName, {
            detail: {
              user: userData,
              appId: appId,
              timestamp: Date.now(),
              delayed: true,
            },
          })
        );
      });
    }, 3000);
  };

  // Register Service Worker for PWA functionality with aggressive cache-busting
  useEffect(() => {
    // Native WebView: skip SW unregister/register churn (web-only PWA path)
    if (Capacitor.isNativePlatform()) return undefined;
    if ('serviceWorker' in navigator) {
      // IMMEDIATELY unregister all old service workers on page load to prevent stale cache
      // This runs synchronously before any service worker can intercept requests
      const unregisterAll = async () => {
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          for (const registration of registrations) {
            await registration.unregister();
            console.log('🗑️ Unregistered old service worker:', registration.scope);
          }
          
          // Also clear all caches
          const cacheNames = await caches.keys();
          await Promise.all(
            cacheNames.map(cacheName => {
              console.log('🗑️ Deleting cache:', cacheName);
              return caches.delete(cacheName);
            })
          );
          console.log('✅ All service workers and caches cleared');
        } catch (error) {
          console.log('Error unregistering service workers:', error);
        }
      };
      
      // Run immediately, don't wait for window load
      unregisterAll();

      // Register new service worker after a short delay to ensure old ones are cleared
      setTimeout(() => {
        navigator.serviceWorker.register('/sw.js')
          .then((registration) => {
            console.log('✅ Service Worker registered successfully:', registration.scope);
            
            // Force update check on every page load to prevent stale cache
            registration.update().catch((error) => {
              console.log('Service Worker update check failed:', error);
            });
            
            // Check for updates
            registration.addEventListener('updatefound', () => {
              const newWorker = registration.installing;
              newWorker.addEventListener('statechange', () => {
                if (newWorker.state === 'installed') {
                  if (navigator.serviceWorker.controller) {
                    // New content is available, automatically reload after 1 second
                    console.log('🔄 New service worker installed, reloading in 1 second...');
                    setTimeout(() => {
                      window.location.reload();
                    }, 1000);
                  } else {
                    // First time installation, no reload needed
                    console.log('✅ Service Worker installed for the first time');
                  }
                }
              });
            });
          })
          .catch((error) => {
            console.log('Service Worker registration failed:', error);
            // Continue without service worker - app should still work
          });
      }, 100);
    }
  }, []);

  useEffect(() => {
    // Check for persistent session first
    const checkPersistentSession = async () => {
      try {
        const storedUser = localStorage.getItem(SESSION_USER_KEY);
        const sessionExpiry = localStorage.getItem('acctoo_session_expiry');

        if (storedUser && sessionExpiry) {
          if (!isSessionExpiryValid(sessionExpiry)) {
            console.log('❌ Session expired, clearing storage');
            clearSessionStorageKeys();
            localStorage.removeItem('acctoo_device_info');
            localStorage.removeItem('acctoo_persistent_refreshed');
            return false;
          }

          const userData = JSON.parse(storedUser);
          const storedUid = userData.uid || userData.userId || userData.firebaseUID;
          console.log('🔄 Found persistent session hint for:', storedUid);

          // SECURITY: Only restore React user state when Firebase Auth already matches.
          // Never trust localStorage alone as proof of authentication.
          const currentAuthUser = firebaseAuth.currentUser;
          if (currentAuthUser && storedUid && currentAuthUser.uid === storedUid) {
            try {
              await reload(currentAuthUser);
              userData.uid = currentAuthUser.uid;
              userData.emailVerified = currentAuthUser.emailVerified;
              userData.isVerified = !!(
                currentAuthUser.emailVerified ||
                currentAuthUser.phoneNumber ||
                currentAuthUser.providerData?.some((p) => p.providerId === 'google.com' || p.providerId === 'phone')
              );
              userData.phoneNumber = currentAuthUser.phoneNumber || userData.phoneNumber || '';
              localStorage.setItem(SESSION_USER_KEY, JSON.stringify(userData));
              writeSessionExpiry();
            } catch (authCheckError) {
              console.warn('⚠️ Could not reload Firebase user during session restore:', authCheckError);
            }

            setUser({ ...userData, uid: currentAuthUser.uid });
            setDb(firebaseDb);
            const chosen = `app_${currentAuthUser.uid}`;
            setAppId(chosen);
            setStoredAppId(chosen);
            setPersistentSessionRestored(true);
            setIsAuthReady(true);

            setTimeout(() => {
              window.dispatchEvent(
                new CustomEvent('userRestoredFromSession', {
                  detail: { user: { ...userData, uid: currentAuthUser.uid }, appId: chosen, timestamp: Date.now() },
                })
              );
              window.dispatchEvent(
                new CustomEvent('companyDataLoad', {
                  detail: { user: { ...userData, uid: currentAuthUser.uid }, appId: chosen, timestamp: Date.now() },
                })
              );
            }, 100);

            return true;
          }

          // Auth not ready yet or UID mismatch — wait for onAuthStateChanged; do not setUser from storage
          console.log('⏳ Persistent session present but Firebase Auth not confirmed yet — deferring to auth listener');
          return false;
        }
      } catch (error) {
        console.error('Error checking persistent session:', error);
        clearSessionStorageKeys();
        localStorage.removeItem('acctoo_device_info');
        localStorage.removeItem('acctoo_persistent_refreshed');
      }
      return false;
    };

    // Try to restore session first, then set up Firebase auth listener
    let sessionRestored = false;
    checkPersistentSession().then((restored) => {
      sessionRestored = restored;
      if (restored) {
        console.log('✅ Persistent session restored, Firebase auth check will still run to verify status');
      }
    }).catch((error) => {
      console.error('Error checking persistent session:', error);
      // Continue to Firebase auth check if persistent session check fails
    });

    // Real Firebase auth via public SSO (always set up listener to catch verification updates)
    console.log('🔄 Setting up Firebase auth listener...');
    const auth = firebaseAuth || getAuth();
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (u) {
        // IMPORTANT: Reload user to get latest emailVerified status
        // This ensures we get the updated status after email verification
        try {
          await reload(u);
          console.log('🔄 User reloaded, emailVerified:', u.emailVerified);
        } catch (reloadError) {
          console.warn('⚠️ Could not reload user (non-critical):', reloadError);
          // Continue anyway - user object might still be valid
        }
      }
      
      console.log('🔄 Auth state changed:', {
        user: u ? u.uid : 'null',
        emailVerified: u?.emailVerified,
        phoneNumber: u?.phoneNumber,
        providerData: u?.providerData?.map(p => p.providerId)
      });
      
      if (u) {
        // Check if we already restored a persistent session for this user
        // BUT: Always update if emailVerified status might have changed
        const persistentUser = localStorage.getItem('acctoo_user');
        if (persistentUser && persistentSessionRestored) {
          const persistentUserData = JSON.parse(persistentUser);
          const storedUid = persistentUserData.uid || persistentUserData.userId || persistentUserData.firebaseUID;
          if (storedUid === u.uid) {
            // CRITICAL: Always check if emailVerified status changed
            // This handles the case where user verified email but persistent session has stale data
            if (persistentUserData.emailVerified !== u.emailVerified) {
              console.log('🔄 Email verification status changed, updating user data...', {
                persistent: persistentUserData.emailVerified,
                firebase: u.emailVerified
              });
              // Continue to update user data below - don't return early
            } else if (persistentUserData.emailVerified === u.emailVerified && u.emailVerified === false) {
              // Both are false, but still update to ensure consistency
              console.log('🔄 Updating user data for consistency...');
              // Continue to update user data below
            } else {
              // Both are true or both are false and same - can skip if truly identical
              // But still allow update if we're on verification-required page (user might have just verified)
              const isOnVerificationPage = window.location.pathname === '/verification-required' || 
                                          window.location.hash.includes('/verification-required');
              if (!isOnVerificationPage) {
                console.log('🔄 Firebase auth detected same user as persistent session, ignoring to prevent duplicate setup');
                return;
              } else {
                console.log('🔄 On verification page, forcing update to check latest status...');
                // Continue to update user data below
              }
            }
          }
        }
        
        // Clear any existing session data to prevent conflicts
        localStorage.removeItem('acctoo_persistent_refreshed');
        
        // Get user profile data to check for phone number and displayName
        let profilePhoneNumber = u.phoneNumber;
        let profileDisplayName = u.displayName;
        
        try {
          // Check user profile for phone number and displayName if not in auth
          const profileDoc = await getDoc(doc(firebaseDb, 'users', u.uid));
          if (profileDoc.exists()) {
            const profileData = profileDoc.data();
            if (!profilePhoneNumber) {
              profilePhoneNumber = profileData.phoneNumber || profileData.phone || '';
              console.log('📱 Found phone in profile:', profilePhoneNumber);
            }
            // Get displayName from Firestore if not in Firebase Auth
            if (!profileDisplayName) {
              profileDisplayName = profileData.displayName || profileData.name || '';
              console.log('👤 Found displayName in profile:', profileDisplayName);
            }
          }
        } catch (error) {
          console.error('Error fetching user profile:', error);
        }
        
        // Verified if email, Google, or Firebase Phone Auth (Auth phoneNumber / phone provider)
        const isVerified = !!(u.emailVerified ||
                              u.phoneNumber ||
                              u.providerData?.some(p => p.providerId === 'google.com' || p.providerId === 'phone'));
        
        // Force trigger auth state change for Google users
        if (u.providerData?.some(p => p.providerId === 'google.com')) {
          console.log('🔄 Google user detected, ensuring proper auth state...');
          // Force a small delay to ensure auth state is fully propagated
          setTimeout(() => {
            console.log('🔄 Google auth state should be fully propagated now');
          }, 500);
        }
        
        console.log('✅ User verification status:', {
          emailVerified: u.emailVerified,
          phoneNumber: profilePhoneNumber,
          isVerified: isVerified,
          providerData: u.providerData?.map(p => p.providerId)
        });
        
        // CRITICAL: Ensure users document exists with emailVerified for Firestore rules
        // This is important even if email was verified before, as the document might not exist
        if (u.emailVerified && firebaseDb) {
          try {
            const userRef = doc(firebaseDb, 'users', u.uid);
            const userSnap = await getDoc(userRef);
            const existingData = userSnap.exists() ? userSnap.data() : {};
            
            // Only update if document doesn't exist or emailVerified is not true
            if (!userSnap.exists() || existingData.emailVerified !== true) {
              await setDoc(userRef, {
                uid: u.uid,
                email: u.email || existingData.email || '',
                displayName: profileDisplayName || u.displayName || existingData.displayName || '',
                phoneNumber: profilePhoneNumber || u.phoneNumber || existingData.phoneNumber || '',
                emailVerified: true, // CRITICAL: Set to true for Firestore rules
                updatedAt: serverTimestamp(),
                createdAt: existingData.createdAt || serverTimestamp(),
                ...existingData // Preserve any existing fields
              }, { merge: true });
              console.log('✅ User document ensured with emailVerified status in auth state change');
            }
          } catch (userDocError) {
            console.warn('⚠️ Could not ensure user document (non-critical):', userDocError);
            // Continue anyway - this is not critical for auth flow
          }
        }
        
        // Force email verification for existing users if not verified
        if (!isVerified && u.email && !u.emailVerified) {
          console.log('⚠️ User not verified, but continuing with limited access');
          // For now, allow access but mark as unverified
          // In production, you might want to force verification
        }
        
        const userData = { 
          uid: u.uid, 
          email: u.email, 
          displayName: profileDisplayName || u.displayName || '',
          emailVerified: u.emailVerified,
          phoneNumber: profilePhoneNumber,
          phoneAuthVerified: !!(u.phoneNumber || u.providerData?.some(p => p.providerId === 'phone')),
          isVerified: isVerified,
          providerData: u.providerData
        };
        
        setUser(userData);
        
        // CRITICAL: Update persistent session whenever user data changes
        // This ensures persistent session always has latest verification status
        try {
          const sessionData = {
            ...userData,
            timestamp: Date.now()
          };
          localStorage.setItem('acctoo_user', JSON.stringify(sessionData));
          console.log('✅ Persistent session updated with user data');
        } catch (sessionError) {
          console.warn('⚠️ Could not update persistent session:', sessionError);
        }
        setDb(firebaseDb);
        // Determine correct appId based on existing data (legacy vs new)
        // Drop legacy artifact checks; always use app_{uid}
        const chosen = `app_${u.uid}`;
        setAppId(chosen);
        setStoredAppId(chosen);

        // Save persistent session (30 days)
        try {
          const sessionData = {
            ...userData,
            loginTime: Date.now(),
            loginMethod: 'firebase'
          };
          
          // Save to Capacitor storage (primary)
          await CapacitorStorage.saveLoginData(sessionData);
          
          // Fallback to localStorage
          localStorage.setItem('acctoo_user', JSON.stringify(sessionData));
          writeSessionExpiry();
          
          // Save device info
          const deviceInfo = {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language,
            timestamp: Date.now(),
            userId: u.uid
          };
          localStorage.setItem('acctoo_device_info', JSON.stringify(deviceInfo));
          
          console.log('✅ Persistent session saved for 30 days');
        } catch (error) {
          console.error('Error saving persistent session:', error);
        }

        // Check if user is owner for current company
        try {
          // We'll check this when company context is available
          setIsOwner(false); // Will be updated when company loads
          
          // Trigger company data loading after authentication - reduced duplicates
          console.log('🔄 Triggering company data refresh after login...');
          
          // Single event dispatch for Firebase auth users
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('userAuthenticated', { 
              detail: { user: userData, appId: chosen, timestamp: Date.now() } 
            }));
          }, 200);
          
        } catch (error) {
          console.error('Error checking ownership:', error);
          setIsOwner(false);
        }

        setIsAuthReady(true);
        
        
      } else {
        console.log('❌ No authenticated user');
        setUser(null);
        // Optional SSO exchange (disabled unless explicitly enabled)
        if (ENABLE_PUBLIC_SSO) {
          try { await signInFromPublicSSO(); } catch (_) {}
        }
        setIsAuthReady(true);
      }
    });
    return () => unsub();
  }, []);




  // Cinematic opening intro (once per session; add ?splash=1 to replay)
  if (!splashComplete) {
    return (
      <AcctooSplashIntro
        forcePlay={new URLSearchParams(window.location.search).get('splash') === '1'}
        onComplete={() => setSplashComplete(true)}
      />
    );
  }

  // Loading State - Show loading with timeout to prevent infinite blank page
  if (!isAuthReady) {
    return (
      <LoadingScreen
        message="Getting things ready..."
        subMessage="Setting up your accounting workspace"
        fullScreen
        showFallbackLink
      />
    );
  }


  // If no user, allow public routes (Login) to render

  // Main Application
  return (
    <ErrorBoundary>
      <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
        <TabProvider>
          <LoadingProvider>
            <NotificationProvider>
              <LanguageProvider>
                <RefreshProvider>
                  <EnhancedCompanyProvider
                    user={user}
                    forceReload={!Capacitor.isNativePlatform()}
                  >
                <AppContent
              user={user}
              isAuthReady={isAuthReady}
              appId={appId}
              db={db}
              handleLogout={async () => {
                try {
                  console.log('🔄 Starting logout process...');
                  
                  // 🔒 PHASE 1: Clear all caches on logout (CRITICAL for security)
                  console.log('🧹 PHASE 1: Clearing all caches on logout');
                  try {
                    const { clearCacheOnLogout } = await import('./utils/secureCacheManager');
                    clearCacheOnLogout();
                  } catch (cacheError) {
                    console.warn('⚠️ Cache clear failed:', cacheError);
                  }
                  
                  // Clear all Firebase auth state first
                  const authInstance = firebaseAuth || getAuth();
                  if (authInstance) {
                    try {
                      // Force sign out from all providers
                      await signOut(authInstance);
                      
                      // Clear any cached Google auth state
                      if (window.gapi && window.gapi.auth2) {
                        const auth2 = window.gapi.auth2.getAuthInstance();
                        if (auth2) {
                          await auth2.signOut();
                        }
                      }
                      
                      // Clear Google auth cookies
                      try {
                        document.cookie = 'g_state=; Max-Age=0; path=/; domain=.google.com';
                        document.cookie = 'g_state=; Max-Age=0; path=/; domain=.googleapis.com';
                        document.cookie = 'g_state=; Max-Age=0; path=/; domain=.firebaseapp.com';
                      } catch (e) {}
                      
                      console.log('✅ Firebase auth signout successful');
                    } catch (authError) {
                      console.log('⚠️ Firebase auth signout failed:', authError);
                    }
                  }
                  
                  // Clear Capacitor storage
                  await CapacitorStorage.clearLoginData();
                  await CapacitorStorage.clearSession();

                  // Native Google / Firebase Auth plugin sign-out
                  try {
                    if (Capacitor.isNativePlatform()) {
                      const { FirebaseAuthentication } = await import('@capacitor-firebase/authentication');
                      if (FirebaseAuthentication?.signOut) {
                        await FirebaseAuthentication.signOut();
                      }
                    }
                  } catch (nativeSignOutError) {
                    console.warn('⚠️ Native auth sign-out skipped:', nativeSignOutError);
                  }

                  // Unified AuthService token cleanup
                  try {
                    const AuthService = (await import('./utils/authService')).default;
                    if (AuthService?.logout) {
                      await AuthService.logout();
                    }
                  } catch (authServiceError) {
                    console.warn('⚠️ AuthService logout skipped:', authServiceError);
                  }

                  try {
                    await SessionStorage.clearMPIN();
                    await SessionStorage.clearAll();
                  } catch (_) {}
                  
                  // Clear all session data (includes MPIN + rate-limit keys)
                  clearSessionStorageKeys();
                  clearSession();
                  localStorage.removeItem('acctoo_device_info');
                  localStorage.removeItem('acctoo_app_id');
                  localStorage.removeItem('acctoo_persistent_refreshed');
                  localStorage.removeItem('acctoo_last_company');
                  
                  // Clear cookies if possible
                  try {
                    document.cookie = 'acctoo_auth_token=; Max-Age=0; path=/; domain=.acctoo.com';
                  } catch (e) {}
                  
                  console.log('✅ All session data cleared on logout');
                  
                  // Reset app state
                  setUser(null);
                  setDb(null);
                  setAppId(null);
                  setIsAuthReady(false);
                  
                  // Force redirect to login
                  const appLoginUrl = process.env.REACT_APP_APP_LOGIN_URL || (typeof window !== 'undefined' ? `${window.location.origin}/login` : 'http://localhost:3000/login');
                  window.location.replace(appLoginUrl);
                  
                } catch (error) {
                  console.error('❌ Logout error:', error);
                  // Even if there's an error, clear data and redirect
                  localStorage.clear();
                  window.location.replace('/login');
                }
              }}
              handleLogin={() => {}}
            />
              </EnhancedCompanyProvider>
            </RefreshProvider>
          </LanguageProvider>
        </NotificationProvider>
          </LoadingProvider>
        </TabProvider>
      </Router>
    </ErrorBoundary>
  );
}

export default App;

// Helper component to host wizard when no active company exists
function FirstTimeCompanyWizard() {
  const { showWizardForFirstTime, setShowWizardForFirstTime, createCompany, userCompanies, loading, companiesLoadAttempted } = useEnhancedCompany();
  const user = firebaseAuth.currentUser;
  const [open, setOpen] = React.useState(false);
  const location = useLocation();

  // Track if user has started creating a company to prevent loops
  const isCreatingCompany = React.useMemo(() => {
    if (!user) return false;
    const creatingFlag = sessionStorage.getItem(`acctoo_creating_company_${user.uid}`);
    const isOnCompanyDetailsPage = location.pathname === '/company-details';
    const hasWizardParam = new URLSearchParams(location.search).get('wizard') === 'create';
    return creatingFlag === 'true' || (isOnCompanyDetailsPage && hasWizardParam);
  }, [user, location.pathname, location.search]);

  React.useEffect(() => { 
    // Never show until we've completed at least one company fetch - prevents modal on every refresh
    if (!companiesLoadAttempted) {
      setOpen(false);
      return;
    }

    // Never show while company data is still loading
    if (loading) {
      setOpen(false);
      return;
    }

    // Don't show if user is already creating a company
    if (isCreatingCompany) {
      setOpen(false);
      return;
    }

    // Only show after load completes: if wizard was explicitly requested (add company) OR user truly has no companies
    const hasNoCompanies = !userCompanies || userCompanies.length === 0;
    if (showWizardForFirstTime || hasNoCompanies) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [showWizardForFirstTime, userCompanies, isCreatingCompany, loading, companiesLoadAttempted]);

  // Don't show modal if user is on verification-required page or company-details page
  // Allow modal to show on dashboard and other pages
  if (!open || !user) return null;
  if (location.pathname === '/verification-required') return null;
  if (isCreatingCompany) return null; // Don't show if user is creating company

  // Check if user already has companies
  const hasExistingCompanies = userCompanies && userCompanies.length > 0;

  const handleComplete = async () => {
    // pull last entered MPIN and policy from wizard (registrationData passed via onComplete)
    // Here we keep it simple: rely on wizard's onComplete forwarding values via event argument
    // We will attach a one-time listener to capture latest values
  };

  const handleWizardComplete = async (data) => {
    await createCompany({ name: data?.ownerName || 'My Company' }, {
      companyMpin: data?.companyMpin || '',
      mpinPolicyRequireSwitch: !!data?.mpinPolicyRequireSwitch,
      mpinPolicyRequireSensitive: !!data?.mpinPolicyRequireSensitive
    });
    setShowWizardForFirstTime(false);
    setOpen(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Welcome to AccToo!</h1>
            <p className="text-gray-600 mb-6">{hasExistingCompanies ? 'Add another company to your account.' : 'Let\'s create your first company to get started.'}</p>
            <div className="space-y-4">
              <button
                onClick={() => {
                  // Mark that user has started creating a company to prevent loop
                  if (user) {
                    sessionStorage.setItem(`acctoo_creating_company_${user.uid}`, 'true');
                  }
                  // Close the welcome modal
                  setOpen(false);
                  setShowWizardForFirstTime(false);
                  // Navigate to CompanyDetails with wizard open
                  window.location.href = '/company-details?wizard=create';
                }}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                🏢 {hasExistingCompanies ? 'Create Your New Company' : 'Create Your First Company'}
              </button>
              <button
                onClick={() => { setShowWizardForFirstTime(false); setOpen(false); }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Skip for now
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
